import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
  Fade,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import axios from 'axios';
import url from '../url.js';
import ActivityInfo from '../models/ActivityInfo';
import colors from '../styles/colors';
import GroupDashboard from './Dashboard/GroupDashboard';
import { getNodeType } from '../utils/nodeTypeUtils';

/**
 * 工具定義 - 便於未來擴展新工具
 * 新增工具只需在此陣列添加新項目，並在 GroupToolPanel 中新增對應的內容區塊
 * 
 * @property {string} id - 工具唯一識別碼
 * @property {string} label - 工具顯示名稱
 * @property {React.ReactNode} icon - 工具圖標
 * @property {string} color - 工具主題色
 */
const TOOLS = [
  { 
    id: 'dashboard', 
    label: '儀表板', 
    icon: <DashboardIcon fontSize="small" />,
    color: '#10B981',
  },
  { 
    id: 'nodes', 
    label: '單元清單', 
    icon: <CategoryIcon fontSize="small" />,
    color: colors.primary.main,
  },
  { 
    id: 'points', 
    label: '夥伴點數', 
    icon: <AccountBalanceWalletIcon fontSize="small" />,
    color: colors.secondary.main,
  },
];

/**
 * 從 HTML 中提取純文本
 * @param {string} html - HTML 內容
 * @returns {string} - 純文本內容
 */
const extractTextFromHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

/**
 * 取得節點預覽文字（依不同節點型別顯示）
 * @param {object} node - 節點資料
 * @returns {string}
 */
const getNodePreviewText = (node) => {
  if (!node) return '';
  const toPreview = (text) => {
    const normalized = (text || '').trim();
    return normalized.substring(0, 50) + (normalized.length > 50 ? '...' : '');
  };

  if (getNodeType(node) === 'questionnaire') {
    try {
      const parsed = JSON.parse(node.content || '{}');
      const description = extractTextFromHtml(parsed?.description || '').trim();
      const questions = Array.isArray(parsed?.questions) ? parsed.questions : [];
      const questionCount = questions.filter((q) => (q?.text || '').trim()).length;

      if (description) return `問卷：${toPreview(description)}`;
      if (questionCount > 0) return `問卷（${questionCount} 題）`;
      return '問卷節點';
    } catch (error) {
      console.warn('Failed to parse questionnaire preview in GroupToolPanel:', error);
      return '問卷節點';
    }
  }

  const plainText = extractTextFromHtml(node.content || '');
  return toPreview(plainText);
};

/**
 * 現代化 Pill 風格 Tab 按鈕
 */
const ToolTab = ({ tool, isActive, onClick }) => (
  <Button
    onClick={onClick}
    startIcon={tool.icon}
    sx={{
      flex: 1,
      py: 1,
      px: 1.5,
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: isActive ? 600 : 400,
      textTransform: 'none',
      color: isActive ? '#fff' : 'text.secondary',
      bgcolor: isActive ? tool.color : 'transparent',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        bgcolor: isActive ? tool.color : alpha(tool.color, 0.1),
        transform: 'translateY(-1px)',
      },
      '& .MuiButton-startIcon': {
        marginRight: 0.5,
      },
    }}
  >
    {tool.label}
  </Button>
);

/**
 * 可拖移的小組工具面板
 * 採用現代 Tab 導航設計，支持多工具擴展
 * 
 * @param {function} onNodeAdded - 節點添加後的回調函數
 * @param {string} activityId - 活動 ID
 * @param {string} groupId - 小組 ID
 * @param {string} mode - 模式（預設 'sticker'）
 * @param {object} ws - WebSocket 連接
 */
const GroupToolPanel = ({ onNodeAdded, activityId, groupId, mode = 'sticker', ws }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeToolId, setActiveToolId] = useState(TOOLS[0].id);
  const [position, setPosition] = useState(() => ({
    x: window.innerWidth * 0.95,
    y: window.innerHeight * 0.90
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingNodeId, setAddingNodeId] = useState(null);
  const [pointsInfo, setPointsInfo] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const panelRef = useRef(null);

  const activeTool = TOOLS.find(t => t.id === activeToolId) || TOOLS[0];
  const isDashboard = activeToolId === 'dashboard';
  const panelWidth = isDashboard ? '85vw' : '25vw';
  const panelMaxHeight = isDashboard ? '85vh' : '75vh';

  // 載入手動群組
  useEffect(() => {
    if (!isOpen || !activityId) return;

    const loadManualGroups = async () => {
      setLoading(true);
      try {
        const activityInfo = await ActivityInfo.get(activityId);
        const settings = activityInfo.settings || {};
        const allGroups = settings.initialNodeGroups || [];
        
        // 只顯示手動群組（非立即顯示的群組）
        const manualGroups = allGroups.filter(g => g.timing !== 'immediate');
        setGroups(manualGroups);
      } catch (error) {
        console.error('載入群組失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    loadManualGroups();
  }, [isOpen, activityId]);

  // 載入儀表板數據
  useEffect(() => {
    if (!isOpen || !activityId) return;

    const loadPointsInfo = async () => {
      setPointsLoading(true);
      try {
        const jwtToken = localStorage.getItem('jwtToken');
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

        // 獲取活動信息以計算所需點數
        const activityInfo = await ActivityInfo.get(activityId);
        const settings = activityInfo.settings || {};
        const partners = settings.partners || [];
        
        // 計算啟用的夥伴數量
        const activePartnerCount = partners.filter(p => p.isActive !== false).length;
        const costPerPartner = 1; // 預設每個夥伴 1 點
        const requiredPoints = activePartnerCount * costPerPartner;

        // 獲取活動創建者的點數（點數扣除基於活動擁有者）
        try {
          const response = await axios.get(
            `${url.backendHost}api/activityInfo/${activityId}/owner-points`,
            { headers, withCredentials: true }
          );

          if (response.data.success) {
            const ownerBalance = response.data.data.balance;
            setPointsInfo({
              currentBalance: ownerBalance,
              requiredPoints,
              activePartnerCount,
              isEnoughPoints: ownerBalance >= requiredPoints,
              pointSystemEnabled: true
            });
          }
        } catch (pointsError) {
          console.error('獲取活動創建者點數失敗:', pointsError);
          // 如果是 404 或 401，可能是點數系統未啟用或未登入
          if (pointsError.response?.status === 404 || pointsError.response?.status === 401) {
            setPointsInfo({
              pointSystemEnabled: false
            });
          } else {
            // 其他錯誤，顯示基本信息但無法獲取餘額
            setPointsInfo({
              currentBalance: null,
              requiredPoints,
              activePartnerCount,
              isEnoughPoints: null,
              pointSystemEnabled: true,
              error: '無法獲取點數資訊'
            });
          }
        }
      } catch (error) {
        console.error('載入點數信息失敗:', error);
        setPointsInfo({
          pointSystemEnabled: false
        });
      } finally {
        setPointsLoading(false);
      }
    };

    loadPointsInfo();
  }, [isOpen, activityId]);

  // 拖動處理
  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('.drag-handle')) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragOffset({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // 限制在視窗範圍內
      const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 300);
      const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 400);
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      
      // 限制在視窗範圍內
      const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 300);
      const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 400);
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // 添加單一節點
  const handleAddNode = async (group, node) => {
    if (!activityId || !groupId) {
      alert('無法獲取活動或小組資訊');
      return;
    }

    setAddingNodeId(node.id);
    try {
      const jwtToken = localStorage.getItem('jwtToken');
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

      const fallbackTag = typeof group?.name === 'string' && group.name.trim() ? group.name : '';
      const nodeTag = typeof node.tags === 'string' && node.tags.trim() ? node.tags : fallbackTag;
      const isQuestionnaireNode = getNodeType(node) === 'questionnaire';

      // 獲取活動內容（用於整合）
      const activityInfo = JSON.parse(localStorage.getItem('activityInfo'));
      const activityContent = activityInfo?.Activity?.content || '';

      // 整合活動說明到節點內容
      const integratedContent = isQuestionnaireNode
        ? node.content || ''
        : activityContent 
        ? `${activityContent}\n\n---\n\n${node.content || ''}`
        : node.content || '';

      // 創建節點（調用 API）
      const nodeType = getNodeType(node);
      const response = await axios.post(
        `${url.backendHost}api/nodes/create`,
        {
          groupId: parseInt(groupId),
          title: node.title || '未命名節點',
          content: integratedContent,
          // Only send type when non-default; NULL in DB is treated as 'text' for backward compat.
          type: nodeType !== 'text' ? nodeType : undefined,
          tags: nodeTag,
          author: '來自小組工具',
        },
        {
          headers,
          withCredentials: true,
        }
      );

      // 發送 WebSocket 事件以通知所有客戶端
      if (ws && response.data.node) {
        ws.emit('create-node', {
          groupId: parseInt(groupId),
          title: node.title || '未命名節點',
          content: integratedContent,
          type: nodeType !== 'text' ? nodeType : undefined,
          tags: nodeTag,
          author: '來自小組工具',
          id: response.data.node.id,
          createdAt: response.data.node.createdAt,
          updatedAt: response.data.node.updatedAt,
          activityId: activityId,
        });
      }

      // 通知父組件節點已添加
      if (onNodeAdded) {
        onNodeAdded(response.data);
      }

      alert(`已添加「${node.title}」到當前小組`);
    } catch (error) {
      console.error('添加節點失敗:', error);
      const errorMsg = error.response?.data?.message || error.message || '添加節點失敗';
      alert(`添加失敗：${errorMsg}`);
    } finally {
      setAddingNodeId(null);
    }
  };

  // 添加整個群組的所有節點
  const handleAddGroupNodes = async (group) => {
    if (!group.nodes || group.nodes.length === 0) {
      alert('此群組沒有節點');
      return;
    }

    const confirmed = window.confirm(
      `確定要添加「${group.name}」的所有 ${group.nodes.length} 個節點嗎？`
    );
    if (!confirmed) return;

    for (const node of group.nodes) {
      await handleAddNode(group, node);
      // 稍微延遲，避免請求過快
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  // 渲染點數管理工具內容
  const renderPointsContent = () => {
    if (pointsLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            載入點數信息中...
          </Typography>
        </Box>
      );
    }

    if (!pointsInfo || !pointsInfo.pointSystemEnabled) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            點數系統未啟用
          </Typography>
        </Box>
      );
    }

    const statusColor = pointsInfo.isEnoughPoints === false 
      ? colors.error 
      : pointsInfo.isEnoughPoints === true 
        ? colors.success 
        : { background: '#F3F4F6', border: '#E5E7EB', text: '#6B7280' };

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: statusColor.background,
          border: `1px solid ${statusColor.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: statusColor.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1rem' }}>🪙</Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: statusColor.text }}>
            夥伴回答點數狀態
          </Typography>
        </Box>

        {pointsInfo.currentBalance !== null ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">活動點數餘額</Typography>
                <Chip 
                  label={`${pointsInfo.currentBalance} 點`}
                  size="small"
                  sx={{
                    bgcolor: '#fff',
                    fontWeight: 600,
                    color: statusColor.text,
                    border: `1px solid ${statusColor.border}`,
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">每次呼叫需要</Typography>
                <Typography variant="body2" fontWeight={600}>{pointsInfo.requiredPoints} 點</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">啟用的夥伴數</Typography>
                <Typography variant="body2" fontWeight={600}>{pointsInfo.activePartnerCount} 個</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box 
              sx={{ 
                py: 1, 
                px: 2, 
                borderRadius: '8px',
                bgcolor: '#fff',
                textAlign: 'center',
              }}
            >
              <Typography 
                variant="body2" 
                fontWeight="bold"
                sx={{ color: statusColor.text }}
              >
                {pointsInfo.isEnoughPoints ? '✓ 點數充足，可以驅動夥伴回答' : '✗ 點數不足，無法驅動夥伴回答'}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            {pointsInfo.error || '無法獲取點數資訊'}
          </Typography>
        )}
      </Box>
    );
  };

  // 渲染儀表板內容
  const renderDashboardContent = () => {
    return <GroupDashboard activityId={activityId} groupId={groupId} />;
  };

  // 渲染節點群組工具內容
  const renderNodesContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            載入群組中...
          </Typography>
        </Box>
      );
    }

    if (groups.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            目前沒有可用的手動群組
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          從下方群組選擇要添加的節點到當前小組
        </Typography>

        {groups.map((group) => (
          <Accordion 
            key={group.id} 
            sx={{ 
              mb: 1,
              borderRadius: '12px !important',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '0 0 8px 0',
              },
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: '12px',
                '&.Mui-expanded': {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ flexGrow: 1, fontWeight: 500 }}>{group.name}</Typography>
                <Chip
                  label={`${group.nodes?.length || 0} 個節點`}
                  size="small"
                  sx={{
                    bgcolor: alpha('#6366F1', 0.1),
                    color: '#6366F1',
                    fontWeight: 500,
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {group.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {group.description}
                </Typography>
              )}

              {group.nodes && group.nodes.length > 0 ? (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<PlaylistAddIcon />}
                    onClick={() => handleAddGroupNodes(group)}
                    sx={{ 
                      mb: 2,
                      borderRadius: '10px',
                      textTransform: 'none',
                    }}
                  >
                    添加全部節點
                  </Button>

                  <Divider sx={{ mb: 1.5 }} />

                  <List dense disablePadding>
                    {group.nodes.map((node) => (
                      <ListItem
                        key={node.id}
                        sx={{
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: '10px',
                          mb: 1,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderColor: '#6366F1',
                          },
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleAddNode(group, node)}
                            disabled={addingNodeId === node.id}
                            aria-label={`添加節點 ${node.title || '未命名'}`}
                            sx={{
                              bgcolor: alpha('#6366F1', 0.1),
                              '&:hover': {
                                bgcolor: alpha('#6366F1', 0.2),
                              },
                            }}
                          >
                            {addingNodeId === node.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <AddIcon sx={{ color: '#6366F1' }} />
                            )}
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={node.title || '未命名'}
                          secondary={getNodePreviewText(node)}
                          primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  此群組沒有節點
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    );
  };

  // 渲染工具內容區
  const renderToolContent = () => {
    switch (activeToolId) {
      case 'dashboard':
        return renderDashboardContent();
      case 'points':
        return renderPointsContent();
      case 'nodes':
        return renderNodesContent();
      default:
        return null;
    }
  };

  // 收合狀態：顯示 FAB 風格按鈕
  if (!isOpen) {
    return (
      <Tooltip title="小組工具" placement="left">
        <Box
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          sx={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            zIndex: 1000,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          <IconButton
            onClick={(e) => {
              if (!isDragging) {
                setIsOpen(true);
              }
            }}
            aria-label="開啟小組工具"
            sx={{
              width: 52,
              height: 52,
              background: 'linear-gradient(135deg, #7C6CFF 0%, #6366F1 100%)',
              color: 'white',
              boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 20px 0 rgba(99, 102, 241, 0.5)',
              },
            }}
          >
            <ConstructionIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  // 展開狀態：顯示完整面板
  return (
    <Fade in={isOpen}>
      <Paper
        ref={panelRef}
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
        elevation={0}
        sx={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: panelWidth,
          maxHeight: panelMaxHeight,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          cursor: isDragging ? 'grabbing' : 'default',
          overflow: 'hidden',
        }}
      >
        {/* 標題欄 */}
        <Box
          className="drag-handle"
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #7C6CFF 0%, #6366F1 100%)',
            color: 'white',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicatorIcon sx={{ opacity: 0.8 }} />
            <Typography variant="subtitle1" fontWeight={600}>小組工具</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            aria-label="關閉小組工具"
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tab 導航區 - Pill 風格 */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#F9FAFB', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1,
              p: 0.5,
              bgcolor: '#fff',
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {TOOLS.map((tool) => (
              <ToolTab
                key={tool.id}
                tool={tool}
                isActive={activeToolId === tool.id}
                onClick={() => setActiveToolId(tool.id)}
              />
            ))}
          </Box>
        </Box>

        {/* 內容區 */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#fff' }}>
          {renderToolContent()}
        </Box>
      </Paper>
    </Fade>
  );
};

export default GroupToolPanel;
